import React, { useState, useEffect, useMemo, useRef } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getFundInvestorRepayments } from '../../services/fundInvestorRepaymentsService';
import { getBrokerDisplayName } from '../../services/brokerDisplayNameService';
import { subscribeAllocationsByFundInvestor, getAllocationsByFundInvestorOnce } from '../../services/fundInvestorPeriodAllocationsService';
import {
  createWithdrawalRequest,
  subscribeWithdrawalRequestsByInvestor,
} from '../../services/fundWithdrawalRequestsService';
import {
  createCapitalAdditionProposal,
  subscribeCapitalAdditionProposalsByInvestor,
} from '../../services/fundCapitalAdditionProposalsService';
import { subscribeUserCompanies } from '../../services/userCompaniesService';
import {
  updateFundInvestor,
  setFundInvestorChequeSpecimen,
  setFundInvestorInvestorSignedContract,
  getFundInvestorFileDownloadUrl,
  summarizeFundInvestorContract,
  uploadQualificationDocFile,
  submitQualificationDocs,
  getQualificationDocsTemplate,
  isFundInvestorAttestationSatisfied,
} from '../../services/fundInvestorsService';
import PlacementQualifiedInvestorAttestation from './PlacementQualifiedInvestorAttestation';
import InvestorFundPeriodReportModal from './InvestorFundPeriodReportModal';
import ProfileModal from '../profile/ProfileModal';
import {
  DollarSign,
  ArrowDownToLine,
  PlusCircle,
  Settings2,
  Banknote,
  LayoutGrid,
  User,
  ClipboardList,
  FileText,
  CheckCircle2,
  MessageCircle,
} from 'lucide-react';
import DirectChatModal from '../directChat/DirectChatModal';
import EscapeCloseLayer from '../shared/EscapeCloseLayer';
import { buildLenderFundInvestorConversationSpec } from '../../services/directChatService';

function formatAmount(n) {
  if (n == null || typeof n !== 'number' || Number.isNaN(n)) return '—';
  const s = String(Math.max(0, Math.floor(n)));
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' $';
}

/** Format number as currency for input display: "1 234.56 $" */
function formatPaidAmountDisplay(value) {
  if (value == null || value === '') return '';
  const num = typeof value === 'number'
    ? value
    : Number(String(value).replace(/\s/g, '').replace(',', '.').replace(/\$/g, ''));
  if (Number.isNaN(num) || num < 0) return '';
  const fixed = num.toFixed(2);
  const [intPart, decPart] = fixed.split('.');
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${withSpaces}.${decPart} $`;
}

function parseAmountDisplay(str) {
  if (!str || typeof str !== 'string') return NaN;
  const cleaned = str.replace(/\s/g, '').replace(/\$/g, '').replace(',', '.').trim();
  return cleaned === '' ? NaN : Number(cleaned);
}

function computeAverageYieldFromAllocations(allocations = []) {
  const last12 = allocations
    .slice()
    .sort((a, b) => String(b.periodKey || '').localeCompare(String(a.periodKey || '')))
    .slice(0, 12);
  if (!last12.length) return null;
  const sumInterest = last12.reduce((sum, a) => sum + (Number(a.allocatedInterest) || 0), 0);
  const avgCapital = last12.reduce((sum, a) => sum + (Number(a.effectiveInvestedAmount) || 0), 0) / last12.length;
  if (!avgCapital || avgCapital <= 0) return null;
  // Annualized based on available months (if <12 months of data).
  const annualized = (sumInterest / avgCapital) * (12 / last12.length) * 100;
  return Math.round(annualized * 100) / 100;
}

function buildInvestingDisplayName(entityType, companyId, companies, personalFallback) {
  const personal = personalFallback || '—';
  if (entityType === 'company' && companyId) {
    const c = (companies || []).find((x) => x.id === companyId);
    return c?.legalName || personal;
  }
  return personal;
}

function formatDateWithLocale(str, locale) {
  if (!str || typeof str !== 'string') return '—';
  const d = str.slice(0, 10);
  if (!d) return '—';
  try {
    const dateLocale = locale === 'fr' ? 'fr-CA' : 'en-CA';
    return new Date(d + 'T12:00:00').toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}

/**
 * My Placements: one row per fund manager (placement). Columns: Fund manager name, Amount invested, Average yield (12 mo.), Manage button.
 * "Manage this placement" opens a modal with everything for that lender: summary, withdrawal requests, capital addition proposals, and buttons to request withdrawal / propose additional funds.
 */
export default function InvestorFundsView({ fundInvestors = [], openFundInvestorId = null }) {
  const { currentUser, userProfile } = useAuth();
  const { t, language } = useLanguage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [capitalProposals, setCapitalProposals] = useState([]);
  const [managePlacement, setManagePlacement] = useState(null);
  const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);
  const [showCapitalForm, setShowCapitalForm] = useState(false);
  const [withdrawalForm, setWithdrawalForm] = useState({ amountRequested: '', comment: '' });
  const [capitalForm, setCapitalForm] = useState({ proposedAmount: '', availableFromDate: '' });
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  const [submittingCapital, setSubmittingCapital] = useState(false);
  const [formError, setFormError] = useState('');
  const [modalRepayments, setModalRepayments] = useState([]);
  const [modalAllocations, setModalAllocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [entityType, setEntityType] = useState('individual');
  const [companyId, setCompanyId] = useState('');
  const [savingDefaultHolder, setSavingDefaultHolder] = useState(false);
  const [defaultHolderMessage, setDefaultHolderMessage] = useState('');
  const [chequeSpecimenUploading, setChequeSpecimenUploading] = useState(false);
  const [chequeSpecimenError, setChequeSpecimenError] = useState('');
  const [chequeSpecimenSuccess, setChequeSpecimenSuccess] = useState('');
  const [investorSignedContractUploading, setInvestorSignedContractUploading] = useState(false);
  const [investorSignedContractError, setInvestorSignedContractError] = useState('');
  const [investorSignedContractSuccess, setInvestorSignedContractSuccess] = useState('');
  const [qualificationDocUploadingKey, setQualificationDocUploadingKey] = useState(null);
  const [qualificationDocsError, setQualificationDocsError] = useState('');
  const [qualificationDocsSuccess, setQualificationDocsSuccess] = useState('');
  /** Merged with Firestore snapshot so the UI shows the file name immediately after upload (snapshot can lag slightly). */
  const [localQualificationUploads, setLocalQualificationUploads] = useState({});
  const [manageModalTab, setManageModalTab] = useState('overview');
  const [periodReportOpen, setPeriodReportOpen] = useState(false);
  const [periodReportKey, setPeriodReportKey] = useState(null);
  const [contractSummaryModalOpen, setContractSummaryModalOpen] = useState(false);
  const [contractSummaryLoading, setContractSummaryLoading] = useState(false);
  const [contractSummaryError, setContractSummaryError] = useState('');
  const [contractSummaryLocal, setContractSummaryLocal] = useState(null);
  /** Profile modal stacked above placement modal (e.g. Mes sociétés). */
  const [stackedProfileModalOpen, setStackedProfileModalOpen] = useState(false);
  /** Single-placement onboarding: auto-open "Manage" once so Holder & banking (attestation) is visible without hunting. */
  const autoOpenedPlacementIdRef = useRef(null);
  /** Optional: open a placement after signup invitation accept. */
  const openedFromInviteRef = useRef(false);
  /** Avoid resetting the modal tab on every fundInvestors snapshot; only when opening a placement (or when fi first arrives). */
  const tabInitializedForPlacementIdRef = useRef(null);
  /** Must be checked before first "save holder" (irreversible for the investor). */
  const [holderCommitAckChecked, setHolderCommitAckChecked] = useState(false);
  /** Unlock void cheque step after user opens contract PDF or AI summary (skipped if no contract file). */
  const [contractSectionAcknowledged, setContractSectionAcknowledged] = useState(false);
  const [directChatModal, setDirectChatModal] = useState(null);

  const directChatUser = useMemo(() => {
    if (!currentUser) return null;
    return {
      ...currentUser,
      displayName: userProfile?.displayName || currentUser.displayName,
    };
  }, [currentUser, userProfile]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    return subscribeWithdrawalRequestsByInvestor(currentUser.uid, setWithdrawalRequests);
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    return subscribeCapitalAdditionProposalsByInvestor(currentUser.uid, setCapitalProposals);
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    return subscribeUserCompanies(currentUser.uid, setCompanies);
  }, [currentUser?.uid]);

  useEffect(() => {
    setChequeSpecimenError('');
    setChequeSpecimenSuccess('');
    setInvestorSignedContractError('');
    setInvestorSignedContractSuccess('');
    setQualificationDocUploadingKey(null);
    setQualificationDocsError('');
    setQualificationDocsSuccess('');
    setLocalQualificationUploads({});
    if (!managePlacement?.id) {
      setManageModalTab('overview');
    }
    setContractSummaryModalOpen(false);
    setContractSummaryLoading(false);
    setContractSummaryError('');
    setContractSummaryLocal(null);
    setHolderCommitAckChecked(false);
    setContractSectionAcknowledged(false);
  }, [managePlacement?.id]);

  // Tab when opening a placement: depends on loaded fundInvestor (e.g. funds_received = no self-attestation step).
  useEffect(() => {
    if (!managePlacement?.id) {
      tabInitializedForPlacementIdRef.current = null;
      return;
    }
    if (tabInitializedForPlacementIdRef.current === managePlacement.id) return;
    const fi = fundInvestors.find((f) => f.id === managePlacement.id);
    if (!fi) return;
    tabInitializedForPlacementIdRef.current = managePlacement.id;
    const needsAttestation = !isFundInvestorAttestationSatisfied(fi);
    if (openedFromInviteRef.current) setManageModalTab('profile');
    else setManageModalTab(needsAttestation ? 'profile' : 'overview');
  }, [managePlacement?.id, fundInvestors]);

  useEffect(() => {
    if (!managePlacement?.id) return;
    const fi = fundInvestors.find((f) => f.id === managePlacement.id);
    if (fi) {
      setEntityType(fi.investingEntityType === 'company' ? 'company' : 'individual');
      setCompanyId(fi.investingCompanyId || '');
    } else {
      setEntityType('individual');
      setCompanyId('');
    }
    setDefaultHolderMessage('');
  }, [managePlacement?.id, fundInvestors]);

  useEffect(() => {
    if (!fundInvestors.length) {
      setRows([]);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const uniqueLenderIds = [...new Set(fundInvestors.map((fi) => fi.lenderUid).filter(Boolean))];
        const lenderNames = {};
        await Promise.all(
          uniqueLenderIds.map(async (uid) => {
            const name = await getBrokerDisplayName(uid);
            lenderNames[uid] = name || uid;
          })
        );
        const next = await Promise.all(
          fundInvestors.map(async (fi) => {
            let totalRepaid = 0;
            let allocations = [];
            try {
              const repayments = await getFundInvestorRepayments(fi.id, { investorUserId: currentUser?.uid });
              totalRepaid = repayments.reduce((s, r) => s + (Number(r.amount) || 0), 0);
            } catch (_) {}
            try {
              allocations = await getAllocationsByFundInvestorOnce(fi.id, { limit: 24 });
            } catch (_) {}
            const invested = Number(fi.investedAmount) || 0;
            const balance = invested - totalRepaid;
            const averageYield = computeAverageYieldFromAllocations(allocations);
            return {
              id: fi.id,
              lenderUid: fi.lenderUid,
              fundManagerName: lenderNames[fi.lenderUid] || fi.lenderUid,
              investedAmount: invested,
              totalRepaid,
              balance,
              averageYield,
            };
          })
        );
        next.sort((a, b) => (a.fundManagerName || '').localeCompare(b.fundManagerName || '', undefined, { sensitivity: 'base' }));
        if (!cancelled) setRows(next);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || t('investorFunds.errorLoad'));
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fundInvestors, currentUser?.uid, t]);

  const withdrawalRequestsForPlacement = useMemo(() => {
    if (!managePlacement?.id) return [];
    return withdrawalRequests.filter((r) => r.fundInvestorId === managePlacement.id);
  }, [managePlacement?.id, withdrawalRequests]);

  const capitalProposalsForPlacement = useMemo(() => {
    if (!managePlacement?.id) return [];
    return capitalProposals.filter((p) => p.fundInvestorId === managePlacement.id);
  }, [managePlacement?.id, capitalProposals]);

  const modalFundInvestor = useMemo(() => {
    if (!managePlacement?.id) return null;
    return fundInvestors.find((f) => f.id === managePlacement.id) || null;
  }, [managePlacement?.id, fundInvestors]);

  const placementQualifiedAttested = useMemo(() => {
    if (!modalFundInvestor) return false;
    return isFundInvestorAttestationSatisfied(modalFundInvestor);
  }, [modalFundInvestor]);

  const serverReadyForAttestation = useMemo(() => {
    if (!modalFundInvestor) return false;
    const serverEt = modalFundInvestor.investingEntityType === 'company' ? 'company' : 'individual';
    if (serverEt === 'company') {
      const cid = modalFundInvestor.investingCompanyId;
      return cid != null && String(cid).trim() !== '';
    }
    return true;
  }, [modalFundInvestor]);

  const holderLocalMatchesServer = useMemo(() => {
    if (!modalFundInvestor) return false;
    const serverEt = modalFundInvestor.investingEntityType === 'company' ? 'company' : 'individual';
    const serverCo = modalFundInvestor.investingCompanyId || '';
    if (serverEt === 'individual') return entityType === 'individual';
    return entityType === 'company' && companyId === serverCo && !!companyId;
  }, [modalFundInvestor, entityType, companyId]);

  const holderCommitRecorded = modalFundInvestor?.investorInvestingHolderCommittedAt != null;
  const holderSelectionLocked = holderCommitRecorded;

  const canSubmitAttestation = useMemo(() => {
    if (!modalFundInvestor || placementQualifiedAttested) return false;
    if (!holderCommitRecorded) return false;
    return serverReadyForAttestation && holderLocalMatchesServer;
  }, [modalFundInvestor, placementQualifiedAttested, holderCommitRecorded, serverReadyForAttestation, holderLocalMatchesServer]);

  const attestationDisabledHint = useMemo(() => {
    if (!modalFundInvestor || placementQualifiedAttested) return '';
    if (!holderCommitRecorded) return t('investorFunds.attestRequiresHolderSave');
    if (!serverReadyForAttestation) return t('fundInvestors.qualified.saveCompanyBeforeAttest');
    if (!holderLocalMatchesServer) return t('fundInvestors.qualified.syncHolderBeforeAttest');
    return '';
  }, [modalFundInvestor, placementQualifiedAttested, holderCommitRecorded, serverReadyForAttestation, holderLocalMatchesServer, t]);

  const qualificationDocsInScope = useMemo(() => {
    if (!modalFundInvestor) return false;
    const s = modalFundInvestor.qualificationDocsRequestStatus || 'not_requested';
    return s === 'requested' || s === 'submitted' || s === 'reviewed';
  }, [modalFundInvestor]);

  const qualificationDocsStepDone = useMemo(() => {
    if (!modalFundInvestor) return true;
    if (!qualificationDocsInScope) return true;
    const s = modalFundInvestor.qualificationDocsRequestStatus;
    return s === 'submitted' || s === 'reviewed';
  }, [modalFundInvestor, qualificationDocsInScope]);

  const contractHasFile = !!modalFundInvestor?.contractFileRef?.storagePath;
  const hasFullyExecutedContract = !!modalFundInvestor?.fullyExecutedContractFileRef?.storagePath;
  const contractStepComplete =
    !contractHasFile || contractSectionAcknowledged || hasFullyExecutedContract;

  const showAttestationSection = holderCommitRecorded || placementQualifiedAttested;
  const showQualificationDocsSection = placementQualifiedAttested && qualificationDocsInScope;
  const showContractSection = placementQualifiedAttested && qualificationDocsStepDone;
  const showChequeSection = showContractSection && contractStepComplete;

  const showHolderFullForm = !holderSelectionLocked;

  const qualificationDocsTemplate = useMemo(() => {
    if (!modalFundInvestor) return [];
    const category =
      modalFundInvestor?.qualificationDocsCategory ||
      modalFundInvestor?.qualifiedInvestorCategory ||
      'accredited';
    return getQualificationDocsTemplate(category);
  }, [modalFundInvestor, modalFundInvestor?.qualificationDocsCategory, modalFundInvestor?.qualifiedInvestorCategory]);

  const qualificationUploadsDisplay = useMemo(() => {
    const server = modalFundInvestor?.qualificationDocsUploads || {};
    return { ...localQualificationUploads, ...server };
  }, [modalFundInvestor?.qualificationDocsUploads, localQualificationUploads]);

  const displayFundContractSummary = useMemo(() => {
    if (!modalFundInvestor) return '';
    const fr = contractSummaryLocal?.fr ?? modalFundInvestor.contractSummaryFr;
    const en = contractSummaryLocal?.en ?? modalFundInvestor.contractSummaryEn;
    if (language.startsWith('fr')) return (fr || en || '').trim();
    return (en || fr || '').trim();
  }, [modalFundInvestor, contractSummaryLocal, language]);

  const modalCapitalAdded = useMemo(() => {
    return capitalProposalsForPlacement
      .filter((p) => p.fundsReceivedAt || p.fundsReceivedAmount != null)
      .reduce((sum, p) => sum + (Number(p.fundsReceivedAmount ?? p.proposedAmount) || 0), 0);
  }, [capitalProposalsForPlacement]);

  const modalInitialInvested = useMemo(() => {
    const invested = managePlacement?.investedAmount ?? 0;
    return Math.max(0, invested - modalCapitalAdded);
  }, [managePlacement?.investedAmount, modalCapitalAdded]);

  useEffect(() => {
    if (!managePlacement?.id || !currentUser?.uid) {
      setModalRepayments([]);
      return;
    }
    let cancelled = false;
    getFundInvestorRepayments(managePlacement.id, { investorUserId: currentUser.uid })
      .then((list) => { if (!cancelled) setModalRepayments(list); })
      .catch(() => { if (!cancelled) setModalRepayments([]); });
    return () => { cancelled = true; };
  }, [managePlacement?.id, currentUser?.uid]);

  useEffect(() => {
    if (!managePlacement?.id || !currentUser?.uid) {
      setModalAllocations([]);
      return () => {};
    }
    return subscribeAllocationsByFundInvestor(managePlacement.id, { limit: 24 }, (items) => {
      setModalAllocations(items || []);
    });
  }, [managePlacement?.id, currentUser?.uid]);

  const fundMovements = useMemo(() => {
    const withdrawals = modalRepayments.map((r) => ({ id: r.id, date: r.repaidAt, amount: r.amount, type: 'withdrawal' }));
    const capitals = capitalProposalsForPlacement
      .filter((p) => p.fundsReceivedAt || p.fundsReceivedAmount != null)
      .map((p) => ({
        id: `cap-${p.id}`,
        date: p.fundsReceivedAt || (p.respondedAt && typeof p.respondedAt === 'string' ? p.respondedAt.slice(0, 10) : ''),
        amount: p.fundsReceivedAmount ?? p.proposedAmount,
        type: 'capital',
      }));
    return [...withdrawals, ...capitals].sort((a, b) => {
      const da = typeof a.date === 'string' ? a.date : (a.date?.toDate?.()?.toISOString?.() ?? a.date?.toISOString?.() ?? '');
      const db = typeof b.date === 'string' ? b.date : (b.date?.toDate?.()?.toISOString?.() ?? b.date?.toISOString?.() ?? '');
      return db.localeCompare(da);
    });
  }, [modalRepayments, capitalProposalsForPlacement]);

  const formatMovementDate = (val) => {
    if (!val) return '—';
    const iso = typeof val === 'string' ? val.slice(0, 10) : (val?.toISOString?.()?.slice(0, 10) || val?.toDate?.()?.toISOString?.()?.slice(0, 10));
    if (!iso) return '—';
    const dateLocale = language === 'fr' ? 'fr-CA' : 'en-CA';
    try {
      return new Date(iso + 'T12:00:00').toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  const personalDisplayName = currentUser?.displayName || currentUser?.email || '—';

  const handleSaveDefaultHolder = async () => {
    if (!managePlacement?.id || !currentUser?.uid) return;
    const fiExisting = fundInvestors.find((f) => f.id === managePlacement.id);
    if (!fiExisting) return;
    if (fiExisting.investorInvestingHolderCommittedAt != null) return;
    if (!holderCommitAckChecked) {
      setFormError(t('investorFunds.holderCommitAckRequired'));
      return;
    }
    if (entityType === 'company' && !companyId) {
      setDefaultHolderMessage('');
      setFormError(t('investorFunds.selectCompany'));
      return;
    }
    setFormError('');
    setSavingDefaultHolder(true);
    setDefaultHolderMessage('');
    try {
      const displayName = buildInvestingDisplayName(entityType, companyId, companies, personalDisplayName);
      const serverEt = fiExisting.investingEntityType === 'company' ? 'company' : 'individual';
      const serverCo = fiExisting.investingCompanyId || '';
      const nextEt = entityType === 'company' ? 'company' : 'individual';
      const nextCo = nextEt === 'company' ? (companyId || '') : '';
      const holderChanged = serverEt !== nextEt || (nextEt === 'company' && nextCo !== serverCo);

      const patch = {
        investingEntityType: entityType === 'company' ? 'company' : 'individual',
        investingCompanyId: entityType === 'company' && companyId ? companyId : null,
        investingDisplayName: displayName,
        investorInvestingHolderCommittedAt: serverTimestamp(),
      };

      // If the holder is changed after attestation (common for staging/legacy imports),
      // reset attestation so the investor can re-attest under the correct holder.
      if (holderChanged && fiExisting?.qualifiedInvestorAttestedAt != null) {
        patch.qualifiedInvestorAttestedAt = null;
        patch.qualifiedInvestorCategory = null;
        patch.qualifiedInvestorAttestationVersion = null;
      }

      await updateFundInvestor(managePlacement.id, patch);

      if (holderChanged && fiExisting?.qualifiedInvestorAttestedAt != null) {
        setDefaultHolderMessage(t('investorFunds.defaultHolderSavedReattest'));
        setManageModalTab('profile');
      } else {
        setDefaultHolderMessage(t('investorFunds.defaultHolderSaved'));
      }
      setHolderCommitAckChecked(false);
      setTimeout(() => setDefaultHolderMessage(''), 6000);
    } catch (err) {
      setFormError(err?.message || t('fundInvestors.invite.errorGeneric'));
    } finally {
      setSavingDefaultHolder(false);
    }
  };

  const handleChequeSpecimenFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !managePlacement?.id) return;
    const maxBytes = 8 * 1024 * 1024;
    if (file.size > maxBytes) {
      setChequeSpecimenError(t('fundInvestors.chequeSpecimen.uploadError'));
      return;
    }
    setChequeSpecimenUploading(true);
    setChequeSpecimenError('');
    setChequeSpecimenSuccess('');
    try {
      await setFundInvestorChequeSpecimen(managePlacement.id, file);
      setChequeSpecimenSuccess(t('fundInvestors.chequeSpecimen.uploadSuccess'));
      setTimeout(() => setChequeSpecimenSuccess(''), 6000);
    } catch (err) {
      setChequeSpecimenError(err?.message || t('fundInvestors.chequeSpecimen.uploadError'));
    } finally {
      setChequeSpecimenUploading(false);
    }
  };

  const handleOpenChequeSpecimen = async (storagePath) => {
    if (!storagePath) return;
    try {
      const url = await getFundInvestorFileDownloadUrl(storagePath);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setChequeSpecimenError(t('fundInvestors.chequeSpecimen.uploadError'));
    }
  };

  const handleOpenQualificationDoc = async (storagePath) => {
    if (!storagePath) return;
    try {
      const url = await getFundInvestorFileDownloadUrl(storagePath);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setQualificationDocsError(t('fundInvestors.qualificationDocs.uploadError'));
    }
  };

  const handleQualificationDocFile = async (docKey, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !managePlacement?.id) return;
    setQualificationDocUploadingKey(docKey);
    setQualificationDocsError('');
    setQualificationDocsSuccess('');
    try {
      const fileRef = await uploadQualificationDocFile(managePlacement.id, docKey, file);
      setLocalQualificationUploads((prev) => ({ ...prev, [docKey]: fileRef }));
      setQualificationDocsSuccess(t('fundInvestors.qualificationDocs.uploadSuccess'));
      setTimeout(() => setQualificationDocsSuccess(''), 6000);
    } catch (err) {
      setQualificationDocsError(err?.message || t('fundInvestors.qualificationDocs.uploadError'));
    } finally {
      setQualificationDocUploadingKey(null);
    }
  };

  const handleSubmitQualificationDocs = async () => {
    if (!managePlacement?.id) return;
    setQualificationDocsError('');
    setQualificationDocsSuccess('');

    // Row state (`managePlacement`) does not include fundInvestor fields — use live doc from subscription.
    const fiDoc = fundInvestors.find((f) => f.id === managePlacement.id) || modalFundInvestor;
    const category =
      fiDoc?.qualificationDocsCategory ||
      fiDoc?.qualifiedInvestorCategory ||
      'accredited';
    const template = getQualificationDocsTemplate(category);
    const uploads = { ...localQualificationUploads, ...(fiDoc?.qualificationDocsUploads || {}) };

    const missing = template
      .filter((it) => it.requireFile)
      .filter((it) => !uploads?.[it.key]?.storagePath);

    if (missing.length > 0) {
      setQualificationDocsError(t('fundInvestors.qualificationDocs.submitMissing'));
      return;
    }

    try {
      await submitQualificationDocs(managePlacement.id);
      setLocalQualificationUploads({});
    } catch (err) {
      setQualificationDocsError(err?.message || t('fundInvestors.qualificationDocs.uploadError'));
    }
  };

  const handleInvestorSignedContractFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !managePlacement?.id) return;
    const maxBytes = 20 * 1024 * 1024;
    if (file.size > maxBytes) {
      setInvestorSignedContractError(t('fundInvestors.contractSigned.uploadError'));
      return;
    }
    if (file.type !== 'application/pdf' && !String(file.name || '').toLowerCase().endsWith('.pdf')) {
      setInvestorSignedContractError(t('fundInvestors.contractSigned.pdfOnly'));
      return;
    }
    setInvestorSignedContractUploading(true);
    setInvestorSignedContractError('');
    setInvestorSignedContractSuccess('');
    try {
      await setFundInvestorInvestorSignedContract(managePlacement.id, file);
      setInvestorSignedContractSuccess(t('fundInvestors.contractSigned.uploadSuccess'));
      setTimeout(() => setInvestorSignedContractSuccess(''), 6000);
    } catch (err) {
      setInvestorSignedContractError(err?.message || t('fundInvestors.contractSigned.uploadError'));
    } finally {
      setInvestorSignedContractUploading(false);
    }
  };

  const handleOpenFundContractPdf = async (storagePath, unlockChequeStep = true) => {
    if (!storagePath) return;
    if (unlockChequeStep) setContractSectionAcknowledged(true);
    try {
      const url = await getFundInvestorFileDownloadUrl(storagePath);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setContractSummaryError(t('privateLenderOffer.summaryError'));
    }
  };

  const handleGenerateFundContractSummary = async () => {
    if (!managePlacement?.id) return;
    setContractSectionAcknowledged(true);
    setContractSummaryError('');
    setContractSummaryLoading(true);
    try {
      const { summaryFr, summaryEn } = await summarizeFundInvestorContract(managePlacement.id);
      setContractSummaryLocal({ fr: summaryFr || '', en: summaryEn || '' });
    } catch (err) {
      const msg = err?.message || err?.code || '';
      setContractSummaryError(
        msg ? `${t('privateLenderOffer.summaryError')} ${msg}` : t('privateLenderOffer.summaryError')
      );
    } finally {
      setContractSummaryLoading(false);
    }
  };

  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();
    if (!managePlacement || !currentUser?.uid) return;
    setFormError('');
    if (entityType === 'company' && !companyId) {
      setFormError(t('investorFunds.selectCompany'));
      return;
    }
    const amount = withdrawalForm.amountRequested.trim() === '' ? null : parseAmountDisplay(withdrawalForm.amountRequested);
    if (amount !== null && (Number.isNaN(amount) || amount < 0)) {
      setFormError(t('fundInvestors.repayments.errorAmount'));
      return;
    }
    const fi = fundInvestors.find((f) => f.id === managePlacement.id);
    if (!fi?.lenderUid) {
      setFormError(t('fundInvestors.invite.errorGeneric'));
      return;
    }
    setSubmittingWithdrawal(true);
    try {
      const investingDisplayName = buildInvestingDisplayName(entityType, companyId, companies, personalDisplayName);
      await createWithdrawalRequest({
        fundInvestorId: managePlacement.id,
        lenderUid: fi.lenderUid,
        investorUserId: currentUser.uid,
        amountRequested: amount,
        comment: withdrawalForm.comment?.trim() || null,
        investingEntityType: entityType === 'company' ? 'company' : 'individual',
        investingCompanyId: entityType === 'company' && companyId ? companyId : null,
        investingDisplayName,
      });
      setShowWithdrawalForm(false);
      setWithdrawalForm({ amountRequested: '', comment: '' });
    } catch (err) {
      setFormError(err?.message || t('fundInvestors.invite.errorGeneric'));
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  const handleCapitalSubmit = async (e) => {
    e.preventDefault();
    if (!managePlacement || !currentUser?.uid) return;
    setFormError('');
    if (entityType === 'company' && !companyId) {
      setFormError(t('investorFunds.selectCompany'));
      return;
    }
    const amount = parseAmountDisplay(capitalForm.proposedAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      setFormError(t('fundInvestors.repayments.errorAmount'));
      return;
    }
    const fi = fundInvestors.find((f) => f.id === managePlacement.id);
    if (!fi?.lenderUid) {
      setFormError(t('fundInvestors.invite.errorGeneric'));
      return;
    }
    if (!isFundInvestorAttestationSatisfied(fi)) {
      setFormError(t('investorFunds.requestsNeedAttestation'));
      return;
    }
    setSubmittingCapital(true);
    try {
      const investingDisplayName = buildInvestingDisplayName(entityType, companyId, companies, personalDisplayName);
      await createCapitalAdditionProposal({
        fundInvestorId: managePlacement.id,
        lenderUid: fi.lenderUid,
        investorUserId: currentUser.uid,
        proposedAmount: amount,
        availableFromDate: capitalForm.availableFromDate?.trim() || null,
        investingEntityType: entityType === 'company' ? 'company' : 'individual',
        investingCompanyId: entityType === 'company' && companyId ? companyId : null,
        investingDisplayName,
      });
      setShowCapitalForm(false);
      setCapitalForm({ proposedAmount: '', availableFromDate: '' });
    } catch (err) {
      setFormError(err?.message || t('fundInvestors.invite.errorGeneric'));
    } finally {
      setSubmittingCapital(false);
    }
  };

  const formatReqDate = (val) => {
    if (!val) return '—';
    const d = val?.toDate?.() ? val.toDate() : val;
    if (d instanceof Date) return formatDateWithLocale(d.toISOString().slice(0, 10), language);
    return formatDateWithLocale(String(d).slice(0, 10), language);
  };

  const attestationOkById = useMemo(() => {
    const m = {};
    fundInvestors.forEach((f) => {
      if (f?.id) m[f.id] = isFundInvestorAttestationSatisfied(f);
    });
    return m;
  }, [fundInvestors]);

  useEffect(() => {
    if (fundInvestors.length === 0 || loading) return;
    if (managePlacement != null) return;
    const pending = rows.filter((r) => !attestationOkById[r.id]);
    // Exactly one placement still needs attestation (onboarding or new lender among several)
    if (pending.length !== 1) return;
    const r = pending[0];
    const fi = fundInvestors.find((f) => f.id === r.id);
    if (!fi || isFundInvestorAttestationSatisfied(fi)) return;
    if (autoOpenedPlacementIdRef.current === r.id) return;
    autoOpenedPlacementIdRef.current = r.id;
    setManagePlacement(r);
    setShowWithdrawalForm(false);
    setShowCapitalForm(false);
    setFormError('');
  }, [fundInvestors, loading, rows, managePlacement, attestationOkById]);

  // After invitation accept: open the intended placement even if there are multiple pending items.
  useEffect(() => {
    if (!openFundInvestorId || typeof openFundInvestorId !== 'string') return;
    if (openedFromInviteRef.current) return;
    if (loading) return;
    if (managePlacement != null) return;
    const r = rows.find((x) => x?.id === openFundInvestorId) || null;
    if (!r) return;
    openedFromInviteRef.current = true;
    autoOpenedPlacementIdRef.current = r.id;
    setManagePlacement(r);
    setShowWithdrawalForm(false);
    setShowCapitalForm(false);
    setFormError('');
  }, [openFundInvestorId, rows, loading, managePlacement]);

  if (fundInvestors.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-indigo-600" />
          {t('investorFunds.title')}
        </h2>
        <p className="text-gray-500">{t('investorFunds.empty')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {fundInvestors.some((f) => !isFundInvestorAttestationSatisfied(f)) && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 shadow-sm">
          {t('fundInvestors.qualified.attestationInModalHint')}
        </div>
      )}
      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-indigo-600" />
          {t('investorFunds.title')}
        </h2>
        {error && (
          <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm mb-4">{error}</p>
        )}
        {loading ? (
          <p className="text-gray-500 py-4">{t('investorFunds.loading')}</p>
        ) : (
          <>
            {rows.length > 0 && (
              <div className="mb-4 py-3 px-4 bg-gray-50 border border-gray-200 rounded-lg flex items-baseline justify-between gap-4">
                <span className="text-sm font-medium text-gray-600">{t('investorFunds.totalPlacements')}</span>
                <span className="text-sm font-semibold text-gray-900 tabular-nums">{formatAmount(rows.reduce((sum, r) => sum + (r.balance || 0), 0))}</span>
              </div>
            )}
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('investorFunds.colFundManager')}</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('investorFunds.colBalance')}</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('investorFunds.colAverageYield')}</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('fundInvestors.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-900">{r.fundManagerName}</span>
                        {!attestationOkById[r.id] && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-900 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded">
                            {t('fundInvestors.qualified.lenderPendingBadge')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">{formatAmount(r.balance)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{r.averageYield != null ? `${r.averageYield}%` : t('investorFunds.yieldNotAvailable')}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        title={!attestationOkById[r.id] ? t('fundInvestors.qualified.completeFirst') : undefined}
                        onClick={() => {
                          setManagePlacement(r);
                          setShowWithdrawalForm(false);
                          setShowCapitalForm(false);
                          setFormError('');
                          setManageModalTab(attestationOkById[r.id] ? 'overview' : 'profile');
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg border border-indigo-200 disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      >
                        <Settings2 className="w-4 h-4" />
                        {t('investorFunds.managePlacement')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* Modal: Manage this placement */}
      {managePlacement && (
        <EscapeCloseLayer
          onClose={() => {
            setManagePlacement(null);
            setShowWithdrawalForm(false);
            setShowCapitalForm(false);
          }}
        >
        <div
          className="fixed inset-0 z-[220] rentalyzer-modal-overlay flex items-center justify-center bg-black/50 p-3 sm:p-6"
          onClick={() => {
            setManagePlacement(null);
            setShowWithdrawalForm(false);
            setShowCapitalForm(false);
          }}
        >
          <div
            className="bg-white rounded-xl shadow-xl border border-gray-200 max-w-3xl w-full min-w-0 max-h-[min(92vh,880px)] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 p-4 sm:p-5 border-b border-gray-200 shrink-0 bg-white">
              <div className="min-w-0 flex-1">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 leading-tight">
                  {t('investorFunds.managePlacement')}
                </h3>
                <p className="text-sm text-indigo-600 font-medium mt-0.5 truncate">{managePlacement.fundManagerName}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {modalFundInvestor?.investorUserId === currentUser?.uid && modalFundInvestor?.lenderUid && (
                  <button
                    type="button"
                    onClick={() => {
                      const spec = buildLenderFundInvestorConversationSpec(
                        managePlacement.id,
                        modalFundInvestor.lenderUid,
                        modalFundInvestor.investorUserId,
                      );
                      setDirectChatModal({
                        conversationId: spec.conversationId,
                        ensureSpec: {
                          participantUids: spec.participantUids,
                          kind: spec.kind,
                          context: spec.context,
                        },
                        title: t('investorFunds.messageManager'),
                        subtitle: managePlacement.fundManagerName || spec.conversationId,
                      });
                    }}
                    className="inline-flex items-center gap-1 px-2 py-1.5 text-sm font-medium text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-50"
                  >
                    <MessageCircle className="w-4 h-4 shrink-0" />
                    <span className="hidden sm:inline">{t('investorFunds.messageManager')}</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setManagePlacement(null);
                    setShowWithdrawalForm(false);
                    setShowCapitalForm(false);
                  }}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 shrink-0"
                >
                  <span className="sr-only">{t('cancel')}</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* KPI strip — toujours visible */}
            <div className="px-4 sm:px-5 py-3 border-b border-gray-100 bg-gradient-to-b from-slate-50 to-white shrink-0">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-2 sm:gap-3">
                <div className="rounded-xl border border-gray-200/80 bg-white p-3 shadow-sm">
                  <span className="text-[11px] sm:text-xs font-medium text-gray-500 uppercase tracking-wide block mb-0.5">
                    {t('fundInvestors.repayments.investedInitially')}
                  </span>
                  <span className="text-base sm:text-lg font-semibold text-gray-900 tabular-nums">{formatAmount(modalInitialInvested)}</span>
                </div>
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3 shadow-sm">
                  <span className="text-[11px] sm:text-xs font-medium text-emerald-800/80 uppercase tracking-wide block mb-0.5">
                    {t('fundInvestors.repayments.capitalAdded')}
                  </span>
                  <span className="text-base sm:text-lg font-semibold text-emerald-900 tabular-nums">{formatAmount(modalCapitalAdded)}</span>
                </div>
                <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-3 shadow-sm">
                  <span className="text-[11px] sm:text-xs font-medium text-amber-900/80 uppercase tracking-wide block mb-0.5">
                    {t('fundInvestors.repayments.totalRepaid')}
                  </span>
                  <span className="text-base sm:text-lg font-semibold text-amber-950 tabular-nums">{formatAmount(managePlacement.totalRepaid)}</span>
                </div>
                <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3 shadow-sm col-span-1">
                  <span className="text-[11px] sm:text-xs font-medium text-sky-900/80 uppercase tracking-wide block mb-0.5">
                    {t('fundInvestors.repayments.balance')}
                  </span>
                  <span className="text-base sm:text-lg font-semibold text-sky-950 tabular-nums">{formatAmount(managePlacement.balance)}</span>
                </div>
              </div>
            </div>

            <div className="flex shrink-0 border-b border-gray-200 px-2 sm:px-3 gap-0.5 bg-gray-50/60" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={manageModalTab === 'overview'}
                onClick={() => setManageModalTab('overview')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 sm:px-3 text-xs sm:text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors min-w-0 ${
                  manageModalTab === 'overview'
                    ? 'border-indigo-600 text-indigo-700 bg-white shadow-sm'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-white/70'
                }`}
              >
                <LayoutGrid className="w-4 h-4 shrink-0 opacity-80" />
                <span className="truncate">{t('investorFunds.modalTabOverview')}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={manageModalTab === 'profile'}
                onClick={() => setManageModalTab('profile')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 sm:px-3 text-xs sm:text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors min-w-0 ${
                  manageModalTab === 'profile'
                    ? 'border-indigo-600 text-indigo-700 bg-white shadow-sm'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-white/70'
                }`}
              >
                <User className="w-4 h-4 shrink-0 opacity-80" />
                <span className="truncate">{t('investorFunds.modalTabProfile')}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={manageModalTab === 'requests'}
                onClick={() => setManageModalTab('requests')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 sm:px-3 text-xs sm:text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors min-w-0 ${
                  manageModalTab === 'requests'
                    ? 'border-indigo-600 text-indigo-700 bg-white shadow-sm'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-white/70'
                }`}
              >
                <ClipboardList className="w-4 h-4 shrink-0 opacity-80" />
                <span className="truncate">{t('investorFunds.modalTabRequests')}</span>
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 sm:p-5 space-y-5">
              {manageModalTab === 'overview' && (
                <>
                  <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('investorFunds.allocatedInterestTitle')}</h4>
                    {modalAllocations.length === 0 ? (
                      <p className="text-sm text-gray-500">{t('investorFunds.allocatedInterestEmpty')}</p>
                    ) : (
                      <div className="border border-gray-200 rounded-lg overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('investorFunds.allocatedMonthCol')}</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">{t('investorFunds.allocatedInterestCol')}</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">{t('investorFunds.allocatedShareCol')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {modalAllocations.map((a) => (
                              <tr key={a.id} className="hover:bg-gray-50/80">
                                <td className="px-3 py-2">
                                  {a.periodKey ? (
                                    <button
                                      type="button"
                                      className="text-indigo-600 hover:underline font-medium text-left"
                                      title={t('investorFunds.openPeriodReport')}
                                      onClick={() => {
                                        setPeriodReportKey(a.periodKey);
                                        setPeriodReportOpen(true);
                                      }}
                                    >
                                      {a.periodKey}
                                    </button>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">{formatPaidAmountDisplay(Number(a.allocatedInterest) || 0)}</td>
                                <td className="px-3 py-2 text-right text-gray-600 tabular-nums">
                                  {a.investorSharePct != null ? `${Math.round(Number(a.investorSharePct) * 10000) / 100}%` : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>

                  <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('fundInvestors.fundMovements.title')}</h4>
                    {fundMovements.length === 0 ? (
                      <p className="text-sm text-gray-500">{t('fundInvestors.fundMovements.empty')}</p>
                    ) : (
                      <div className="border border-gray-200 rounded-lg overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('fundInvestors.fundMovements.date')}</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">{t('fundInvestors.fundMovements.amount')}</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('fundInvestors.fundMovements.type')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {fundMovements.map((m) => (
                              <tr key={m.id} className="hover:bg-gray-50/80">
                                <td className="px-3 py-2 whitespace-nowrap">{formatMovementDate(m.date)}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{formatAmount(m.amount)}</td>
                                <td className="px-3 py-2">
                                  {m.type === 'withdrawal' ? t('fundInvestors.fundMovements.typeWithdrawal') : t('fundInvestors.fundMovements.typeCapital')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                </>
              )}

              {manageModalTab === 'profile' && (
                <div className="space-y-5">
                  <section className="rounded-xl border border-indigo-100 bg-indigo-50/35 p-4 shadow-sm space-y-3">
                    <h4 className="text-sm font-semibold text-gray-900">
                      <span className="text-indigo-600 font-bold mr-1.5">1.</span>
                      {t('investorFunds.investingEntity')}
                    </h4>
                    {showHolderFullForm ? (
                      <>
                        {placementQualifiedAttested && (
                          <p className="text-xs text-gray-800 bg-white/80 border border-indigo-200/60 rounded-lg px-3 py-2 leading-relaxed">
                            {t('fundInvestors.qualified.holderChangeResetsAttestationHint')}
                          </p>
                        )}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <select
                            value={entityType}
                            onChange={(e) => {
                              setEntityType(e.target.value);
                              setDefaultHolderMessage('');
                              setHolderCommitAckChecked(false);
                            }}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 bg-white disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <option value="individual">{t('investorFunds.investingIndividual')}</option>
                            <option value="company">{t('investorFunds.investingCompany')}</option>
                          </select>
                          {entityType === 'company' && (
                            <select
                              value={companyId}
                              onChange={(e) => {
                                setCompanyId(e.target.value);
                                setDefaultHolderMessage('');
                                setHolderCommitAckChecked(false);
                              }}
                              className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 bg-white disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              <option value="">{t('investorFunds.selectCompany')}</option>
                              {companies.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.legalName || c.id}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        {entityType === 'company' && (
                          <div className="rounded-lg border border-indigo-100 bg-white/90 px-3 py-2.5 space-y-1">
                            <button
                              type="button"
                              onClick={() => setStackedProfileModalOpen(true)}
                              className="text-sm font-medium text-indigo-700 hover:text-indigo-900 hover:underline text-left"
                            >
                              {t('investorFunds.openProfileCompaniesLink')}
                            </button>
                            <p className="text-xs text-gray-600 leading-relaxed">{t('investorFunds.openProfileCompaniesHelp')}</p>
                          </div>
                        )}
                        {entityType === 'company' && companies.length === 0 && (
                          <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{t('investorFunds.noCompaniesHint')}</p>
                        )}
                        <p className="text-xs text-amber-950 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 leading-relaxed">
                          {t('investorFunds.holderSaveIrreversibleWarning')}
                        </p>
                        <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            checked={holderCommitAckChecked}
                            onChange={(e) => setHolderCommitAckChecked(e.target.checked)}
                          />
                          <span>{t('investorFunds.holderSaveConfirmCheckbox')}</span>
                        </label>
                        <button
                          type="button"
                          onClick={handleSaveDefaultHolder}
                          disabled={savingDefaultHolder || !holderCommitAckChecked}
                          className="text-sm px-4 py-2 bg-white border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 disabled:opacity-50 font-medium"
                        >
                          {savingDefaultHolder ? t('loading') : t('investorFunds.saveDefaultHolder')}
                        </button>
                        {defaultHolderMessage && (
                          <div className="text-xs text-green-800 space-y-1">
                            <p className="font-medium">{defaultHolderMessage}</p>
                            <p className="text-green-900/90">{t('investorFunds.defaultHolderSavedLockedReminder')}</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                        <div className="flex items-start gap-2 text-sm text-emerald-950 min-w-0">
                          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" aria-hidden />
                          <div className="space-y-1 min-w-0">
                            <p className="font-medium">
                              {entityType === 'company' && companyId
                                ? companies.find((c) => c.id === companyId)?.legalName || companyId
                                : t('investorFunds.investingIndividual')}
                            </p>
                            <p className="text-xs text-gray-700 leading-relaxed">{t('investorFunds.holderLockedReadOnlyHint')}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </section>

                  {showAttestationSection && modalFundInvestor && (
                    <section className="rounded-xl border border-amber-200 bg-amber-50/30 p-4 shadow-sm space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900">
                        <span className="text-indigo-600 font-bold mr-1.5">2.</span>
                        {t('investorFunds.holderBankingStepAttestation')}
                      </h4>
                      {placementQualifiedAttested && (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-2">
                          <p className="text-sm font-medium text-emerald-900 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" />
                            {t('fundInvestors.qualified.attestedSummaryTitle')}
                          </p>
                          {modalFundInvestor.qualifiedInvestorAttestedAt != null ? (
                            <>
                              <p className="text-sm text-gray-800">
                                <span className="font-medium">{t('fundInvestors.qualified.attestedOn')}:</span>{' '}
                                {formatMovementDate(modalFundInvestor.qualifiedInvestorAttestedAt)}
                              </p>
                              <p className="text-sm text-gray-800">
                                <span className="font-medium">{t('fundInvestors.qualified.lenderFieldLabel')}:</span>{' '}
                                {modalFundInvestor.qualifiedInvestorCategory === 'eligible'
                                  ? t('fundInvestors.qualified.lenderCategoryEligible')
                                  : modalFundInvestor.qualifiedInvestorCategory === 'institution'
                                    ? t('fundInvestors.qualified.lenderCategoryInstitution')
                                    : t('fundInvestors.qualified.lenderCategoryAccredited')}
                              </p>
                            </>
                          ) : (
                            <p className="text-sm text-gray-800 leading-relaxed">
                              {t('investorFunds.qualifiedViaActivePlacementBody')}
                            </p>
                          )}
                          {modalFundInvestor.qualifiedInvestorAttestedAt == null &&
                            modalFundInvestor.qualifiedInvestorCategory && (
                              <p className="text-sm text-gray-800">
                                <span className="font-medium">{t('fundInvestors.qualified.lenderFieldLabel')}:</span>{' '}
                                {modalFundInvestor.qualifiedInvestorCategory === 'eligible'
                                  ? t('fundInvestors.qualified.lenderCategoryEligible')
                                  : modalFundInvestor.qualifiedInvestorCategory === 'institution'
                                    ? t('fundInvestors.qualified.lenderCategoryInstitution')
                                    : t('fundInvestors.qualified.lenderCategoryAccredited')}
                              </p>
                            )}
                        </div>
                      )}
                      {!placementQualifiedAttested && (
                        <PlacementQualifiedInvestorAttestation
                          fundInvestorId={managePlacement.id}
                          t={t}
                          disabled={!canSubmitAttestation}
                          disabledHint={attestationDisabledHint}
                          hideHeader
                        />
                      )}
                    </section>
                  )}

                  {showQualificationDocsSection && modalFundInvestor && (
                    <section className="rounded-xl border border-amber-100 bg-amber-50/40 p-4 shadow-sm space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <span className="text-indigo-600 font-bold mr-1">3.</span>
                        <ClipboardList className="w-4 h-4 text-amber-700 shrink-0" />
                        {t('fundInvestors.qualificationDocs.title')}
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed">{t('fundInvestors.qualificationDocs.intro')}</p>

                      <p className="text-xs text-gray-500">
                        {t('fundInvestors.qualificationDocs.requestedCategory')}:{' '}
                        {modalFundInvestor.qualificationDocsCategory || modalFundInvestor.qualifiedInvestorCategory || 'accredited'}
                      </p>

                      {(() => {
                        const requestStatus = modalFundInvestor?.qualificationDocsRequestStatus || 'not_requested';
                        const qualificationFilesReadOnlyList = (
                          <ul className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
                            {qualificationDocsTemplate.map((it) => {
                              const uploaded = qualificationUploadsDisplay?.[it.key];
                              return (
                                <li
                                  key={it.key}
                                  className="px-3 py-2.5 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between gap-y-1"
                                >
                                  <span className="text-sm text-gray-800">{t(it.labelKey)}</span>
                                  {uploaded?.storagePath ? (
                                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                                      <span className="text-sm text-emerald-800 font-medium break-all">{uploaded.name || '—'}</span>
                                      <button
                                        type="button"
                                        onClick={() => handleOpenQualificationDoc(uploaded.storagePath)}
                                        className="text-sm text-indigo-700 font-medium hover:underline shrink-0"
                                      >
                                        {t('fundInvestors.qualificationDocs.open')}
                                      </button>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400">—</span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        );
                        if (requestStatus === 'not_requested') {
                          return <p className="text-sm text-gray-600">{t('fundInvestors.qualificationDocs.status_not_requested')}</p>;
                        }
                        if (requestStatus === 'requested') {
                          return (
                            <>
                              <div className="space-y-2">
                                {qualificationDocsTemplate.map((it) => {
                                  const uploaded = qualificationUploadsDisplay?.[it.key];
                                  return (
                                    <div key={it.key} className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
                                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                        <span className="text-sm font-medium text-gray-900">{t(it.labelKey)}</span>
                                        {uploaded?.storagePath ? (
                                          <div className="flex flex-col gap-1 sm:items-end min-w-0">
                                            <span className="inline-flex items-center gap-1.5 text-sm text-emerald-800 font-medium">
                                              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" aria-hidden />
                                              <span className="break-all text-left sm:text-right">
                                                {uploaded.name || t('fundInvestors.qualificationDocs.fileOnFile')}
                                              </span>
                                            </span>
                                            <button
                                              type="button"
                                              onClick={() => handleOpenQualificationDoc(uploaded.storagePath)}
                                              className="text-sm text-indigo-700 font-medium hover:underline w-fit"
                                            >
                                              {t('fundInvestors.qualificationDocs.open')}
                                            </button>
                                          </div>
                                        ) : (
                                          <span className="text-xs text-gray-500">{t('fundInvestors.qualificationDocs.notUploaded')}</span>
                                        )}
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                          {uploaded?.storagePath
                                            ? t('fundInvestors.qualificationDocs.replaceLabel')
                                            : t('fundInvestors.qualificationDocs.uploadLabel')}
                                        </label>
                                        <input
                                          type="file"
                                          accept="image/*,application/pdf,.pdf"
                                          disabled={qualificationDocUploadingKey === it.key}
                                          onChange={(e) => handleQualificationDocFile(it.key, e)}
                                          className="w-full text-sm text-gray-600"
                                        />
                                        {qualificationDocUploadingKey === it.key && (
                                          <p className="text-xs text-gray-500 mt-1">{t('fundInvestors.qualificationDocs.uploading')}</p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              {qualificationDocsError && (
                                <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                  {qualificationDocsError}
                                </p>
                              )}
                              {qualificationDocsSuccess && (
                                <p className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
                                  {qualificationDocsSuccess}
                                </p>
                              )}

                              <button
                                type="button"
                                onClick={handleSubmitQualificationDocs}
                                className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                              >
                                {t('fundInvestors.qualificationDocs.submitButton')}
                              </button>
                            </>
                          );
                        }
                        if (requestStatus === 'submitted' || requestStatus === 'reviewed') {
                          return (
                            <div className="space-y-3">
                              <p className="text-sm text-gray-700">
                                {requestStatus === 'submitted'
                                  ? t('fundInvestors.qualificationDocs.status_submitted')
                                  : t('fundInvestors.qualificationDocs.status_reviewed')}
                              </p>
                              <p className="text-xs font-medium text-gray-600">{t('fundInvestors.qualificationDocs.filesOnRecord')}</p>
                              {qualificationFilesReadOnlyList}
                            </div>
                          );
                        }
                        return (
                          <div className="space-y-3">
                            <p className="text-sm text-gray-700">{t('fundInvestors.qualificationDocs.status_reviewed')}</p>
                            <p className="text-xs font-medium text-gray-600">{t('fundInvestors.qualificationDocs.filesOnRecord')}</p>
                            {qualificationFilesReadOnlyList}
                          </div>
                        );
                      })()}
                    </section>
                  )}

                  {showContractSection && modalFundInvestor && (
                    <section className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 shadow-sm space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                        <span className="text-indigo-600 font-bold mr-1">4.</span>
                        <FileText className="w-4 h-4 text-slate-700 shrink-0" />
                        {t('investorFunds.contractSectionTitle')}
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed">{t('fundInvestors.contractSigned.investorWorkflowHint')}</p>
                      {modalFundInvestor.fullyExecutedContractFileRef?.storagePath && (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 space-y-2">
                          <p className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 shrink-0" />
                            {t('fundInvestors.contractSigned.fullyExecutedAvailable')}
                          </p>
                          <button
                            type="button"
                            onClick={() => handleOpenFundContractPdf(modalFundInvestor.fullyExecutedContractFileRef.storagePath)}
                            className="text-sm text-indigo-700 font-medium hover:underline"
                          >
                            {t('fundInvestors.contractSigned.downloadFullyExecuted')}
                          </button>
                          {modalFundInvestor.fullyExecutedContractFileRef?.name && (
                            <p className="text-xs text-gray-600">{modalFundInvestor.fullyExecutedContractFileRef.name}</p>
                          )}
                        </div>
                      )}
                      {contractHasFile && !contractSectionAcknowledged && !hasFullyExecutedContract && (
                        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          {t('investorFunds.holderBankingContractUnlockHint')}
                        </p>
                      )}
                      {modalFundInvestor.contractFileRef?.storagePath ? (
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-medium text-gray-700 mb-1">{t('fundInvestors.contractSigned.templateLabel')}</p>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                              <button
                                type="button"
                                onClick={() => handleOpenFundContractPdf(modalFundInvestor.contractFileRef.storagePath)}
                                className="text-indigo-700 font-medium hover:underline"
                              >
                                {t('privateLenderOffer.downloadContract')}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setContractSectionAcknowledged(true);
                                  setContractSummaryError('');
                                  setContractSummaryModalOpen(true);
                                }}
                                className="text-indigo-700 font-medium hover:underline"
                              >
                                {t('privateLenderOffer.summarizeWithAI')}
                              </button>
                            </div>
                          </div>
                          <div className="border-t border-gray-200 pt-3 space-y-2">
                            <label className="block text-xs font-medium text-gray-700">
                              {modalFundInvestor.investorSignedContractFileRef?.storagePath
                                ? t('fundInvestors.contractSigned.replaceSignedUpload')
                                : t('fundInvestors.contractSigned.uploadSignedLabel')}
                            </label>
                            {modalFundInvestor.investorSignedContractFileRef?.storagePath && (
                              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-800">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" aria-hidden />
                                <span className="font-medium break-all">{modalFundInvestor.investorSignedContractFileRef.name || 'PDF'}</span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleOpenFundContractPdf(modalFundInvestor.investorSignedContractFileRef.storagePath, false)
                                  }
                                  className="text-indigo-700 font-medium hover:underline"
                                >
                                  {t('fundInvestors.contractSigned.openMySigned')}
                                </button>
                              </div>
                            )}
                            <input
                              type="file"
                              accept="application/pdf,.pdf"
                              disabled={investorSignedContractUploading}
                              onChange={handleInvestorSignedContractFile}
                              className="w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded-lg file:border file:border-gray-300 file:bg-white file:text-sm disabled:opacity-50"
                            />
                            {investorSignedContractUploading && (
                              <p className="text-xs text-gray-500">{t('fundInvestors.contractSigned.uploading')}</p>
                            )}
                            {investorSignedContractError && (
                              <p className="text-xs text-red-600">{investorSignedContractError}</p>
                            )}
                            {investorSignedContractSuccess && (
                              <p className="text-xs text-green-700 font-medium">{investorSignedContractSuccess}</p>
                            )}
                            <p className="text-xs text-gray-500">{t('fundInvestors.contractSigned.waitForManager')}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600">{t('investorFunds.contractNoFile')}</p>
                      )}
                    </section>
                  )}

                  {showChequeSection && modalFundInvestor && (
                    <section className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4 shadow-sm space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                        <span className="text-indigo-600 font-bold mr-1">5.</span>
                        <Banknote className="w-4 h-4 text-emerald-700 shrink-0" />
                        {t('fundInvestors.chequeSpecimen.title')}
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed">{t('fundInvestors.chequeSpecimen.hint')}</p>
                      {modalFundInvestor.chequeSpecimenFileRef?.storagePath ? (
                        <div className="text-sm text-gray-700 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span>
                              {t('fundInvestors.chequeSpecimen.currentFile')}:{' '}
                              <span className="font-medium">{modalFundInvestor.chequeSpecimenFileRef.name || '—'}</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => handleOpenChequeSpecimen(modalFundInvestor.chequeSpecimenFileRef.storagePath)}
                              className="text-sm text-emerald-700 font-medium hover:underline"
                            >
                              {t('fundInvestors.chequeSpecimen.open')}
                            </button>
                          </div>
                          {modalFundInvestor.chequeSpecimenUpdatedAt && (
                            <p className="text-xs text-gray-500">
                              {t('fundInvestors.chequeSpecimen.updatedAt')}: {formatMovementDate(modalFundInvestor.chequeSpecimenUpdatedAt)}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-amber-900/90">{t('fundInvestors.chequeSpecimen.none')}</p>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('fundInvestors.chequeSpecimen.upload')}</label>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf,.pdf"
                          disabled={chequeSpecimenUploading}
                          onChange={handleChequeSpecimenFile}
                          className="w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded-lg file:border file:border-gray-300 file:bg-white file:text-sm disabled:opacity-50"
                        />
                        {chequeSpecimenUploading && (
                          <p className="text-xs text-gray-500 mt-1">{t('fundInvestors.chequeSpecimen.uploading')}</p>
                        )}
                      </div>
                      {chequeSpecimenError && <p className="text-xs text-red-600">{chequeSpecimenError}</p>}
                      {chequeSpecimenSuccess && <p className="text-xs text-green-700 font-medium">{chequeSpecimenSuccess}</p>}
                    </section>
                  )}
                </div>
              )}

              {manageModalTab === 'requests' && (
                <div className="space-y-5">
                  {!placementQualifiedAttested && (
                    <p className="text-sm text-amber-950 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      {t('investorFunds.requestsNeedAttestation')}
                    </p>
                  )}
                  <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={!placementQualifiedAttested}
                      title={!placementQualifiedAttested ? t('investorFunds.requestsNeedAttestation') : undefined}
                      onClick={() => {
                        setShowWithdrawalForm(true);
                        setShowCapitalForm(false);
                        setWithdrawalForm({ amountRequested: '', comment: '' });
                        setFormError('');
                      }}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-indigo-700 bg-white border border-indigo-200 rounded-xl hover:bg-indigo-50 shadow-sm disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-white"
                    >
                      <ArrowDownToLine className="w-4 h-4" />
                      {t('withdrawalRequests.requestButton')}
                    </button>
                    <button
                      type="button"
                      disabled={!placementQualifiedAttested}
                      title={!placementQualifiedAttested ? t('investorFunds.requestsNeedAttestation') : undefined}
                      onClick={() => {
                        setShowCapitalForm(true);
                        setShowWithdrawalForm(false);
                        setCapitalForm({ proposedAmount: '', availableFromDate: '' });
                        setFormError('');
                      }}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-indigo-700 bg-white border border-indigo-200 rounded-xl hover:bg-indigo-50 shadow-sm disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-white"
                    >
                      <PlusCircle className="w-4 h-4" />
                      {t('capitalAddition.proposeButton')}
                    </button>
                  </div>

                  {showWithdrawalForm && (
                    <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 shadow-sm">
                      <h4 className="font-semibold text-gray-900 mb-3">{t('withdrawalRequests.requestButton')}</h4>
                      <form onSubmit={handleWithdrawalSubmit} className="space-y-3">
                        {formError && <p className="text-sm text-red-600">{formError}</p>}
                        <p className="text-xs text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2">
                          <span className="font-medium text-gray-700">{t('investorFunds.investingEntity')}: </span>
                          {entityType === 'company' && companyId
                            ? companies.find((c) => c.id === companyId)?.legalName || companyId
                            : t('investorFunds.investingIndividual')}
                        </p>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('withdrawalRequests.amountOptional')}</label>
                          <input
                            type="text"
                            value={withdrawalForm.amountRequested}
                            onChange={(e) => setWithdrawalForm((f) => ({ ...f, amountRequested: e.target.value }))}
                            onBlur={(e) => {
                              const raw = e.target.value.trim();
                              if (raw === '') {
                                setWithdrawalForm((f) => ({ ...f, amountRequested: '' }));
                                return;
                              }
                              const n = parseAmountDisplay(raw);
                              if (!Number.isNaN(n) && n >= 0) setWithdrawalForm((f) => ({ ...f, amountRequested: formatPaidAmountDisplay(n) }));
                            }}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                            placeholder="0.00 $"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('withdrawalRequests.commentOptional')}</label>
                          <textarea
                            value={withdrawalForm.comment}
                            onChange={(e) => setWithdrawalForm((f) => ({ ...f, comment: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[72px] bg-white"
                            placeholder={t('withdrawalRequests.commentPlaceholder')}
                            rows={2}
                          />
                        </div>
                        <div className="flex flex-wrap gap-2 pt-1">
                          <button
                            type="button"
                            onClick={() => setShowWithdrawalForm(false)}
                            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg border border-gray-300 bg-white"
                          >
                            {t('cancel')}
                          </button>
                          <button
                            type="submit"
                            disabled={submittingWithdrawal}
                            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
                          >
                            {submittingWithdrawal ? t('withdrawalRequests.submitting') : t('withdrawalRequests.submit')}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {showCapitalForm && (
                    <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 shadow-sm">
                      <h4 className="font-semibold text-gray-900 mb-3">{t('capitalAddition.proposeButton')}</h4>
                      <form onSubmit={handleCapitalSubmit} className="space-y-3">
                        {formError && <p className="text-sm text-red-600">{formError}</p>}
                        <p className="text-xs text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2">
                          <span className="font-medium text-gray-700">{t('investorFunds.investingEntity')}: </span>
                          {entityType === 'company' && companyId
                            ? companies.find((c) => c.id === companyId)?.legalName || companyId
                            : t('investorFunds.investingIndividual')}
                        </p>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('capitalAddition.proposedAmount')} *</label>
                          <input
                            type="text"
                            value={capitalForm.proposedAmount}
                            onChange={(e) => setCapitalForm((f) => ({ ...f, proposedAmount: e.target.value }))}
                            onBlur={(e) => {
                              const raw = e.target.value.trim();
                              if (raw === '') {
                                setCapitalForm((f) => ({ ...f, proposedAmount: '' }));
                                return;
                              }
                              const n = parseAmountDisplay(raw);
                              if (!Number.isNaN(n) && n >= 0) setCapitalForm((f) => ({ ...f, proposedAmount: formatPaidAmountDisplay(n) }));
                            }}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                            placeholder="0.00 $"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('capitalAddition.availableFromDate')}</label>
                          <input
                            type="date"
                            value={capitalForm.availableFromDate}
                            onChange={(e) => setCapitalForm((f) => ({ ...f, availableFromDate: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                          />
                        </div>
                        <div className="flex flex-wrap gap-2 pt-1">
                          <button
                            type="button"
                            onClick={() => setShowCapitalForm(false)}
                            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg border border-gray-300 bg-white"
                          >
                            {t('cancel')}
                          </button>
                          <button
                            type="submit"
                            disabled={submittingCapital}
                            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
                          >
                            {submittingCapital ? t('capitalAddition.submitting') : t('capitalAddition.submit')}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <ArrowDownToLine className="w-4 h-4 text-gray-500" />
                      {t('withdrawalRequests.title')}
                    </h4>
                    {withdrawalRequestsForPlacement.length === 0 ? (
                      <p className="text-sm text-gray-500">{t('withdrawalRequests.empty')}</p>
                    ) : (
                      <ul className="divide-y divide-gray-100">
                        {withdrawalRequestsForPlacement.map((req) => (
                          <li key={req.id} className="py-3 first:pt-0">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1">
                              <span className="text-sm text-gray-900">
                                {formatReqDate(req.requestedAt)} — {req.amountRequested != null ? formatAmount(req.amountRequested) : '—'}
                              </span>
                              <span className="flex items-center gap-2 shrink-0">
                                {req.status === 'completed' && req.repaidAt ? (
                                  <span className="text-xs text-green-700 font-medium">
                                    {t('withdrawalRequests.repaidOn')} {formatMovementDate(req.repaidAt)}
                                  </span>
                                ) : (
                                  <span
                                    className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${
                                      req.status === 'completed'
                                        ? 'bg-green-100 text-green-800'
                                        : req.status === 'cancelled'
                                          ? 'bg-gray-100 text-gray-600'
                                          : 'bg-amber-100 text-amber-800'
                                    }`}
                                  >
                                    {t(
                                      `withdrawalRequests.status${
                                        req.status === 'completed' ? 'Completed' : req.status === 'cancelled' ? 'Cancelled' : 'Pending'
                                      }`,
                                    )}
                                  </span>
                                )}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {t('fundInvestors.investingHolder')}:{' '}
                              {req.investingDisplayName ||
                                (req.investingEntityType === 'company' ? req.investingCompanyId || '—' : t('fundInvestors.investingPersonal'))}
                            </p>
                            {req.comment && (
                              <p className="text-xs text-gray-600 mt-1 italic">
                                {t('withdrawalRequests.investorComment')}: {req.comment}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>

                  <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <PlusCircle className="w-4 h-4 text-gray-500" />
                      {t('capitalAddition.title')}
                    </h4>
                    {capitalProposalsForPlacement.length === 0 ? (
                      <p className="text-sm text-gray-500">{t('capitalAddition.empty')}</p>
                    ) : (
                      <ul className="divide-y divide-gray-100">
                        {capitalProposalsForPlacement.map((p) => (
                          <li key={p.id} className="py-3 first:pt-0">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1">
                              <span className="text-sm text-gray-900">
                                {formatAmount(p.proposedAmount)} — {p.availableFromDate ? formatDateWithLocale(p.availableFromDate, language) : '—'}
                              </span>
                              <span className="shrink-0">
                                {p.fundsReceivedAt ? (
                                  <span className="text-xs text-green-700 font-medium">
                                    {t('fundInvestors.transaction.receivedOn')} {formatMovementDate(p.fundsReceivedAt)}
                                  </span>
                                ) : (
                                  <span
                                    className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${
                                      p.status === 'accepted'
                                        ? 'bg-green-100 text-green-800'
                                        : p.status === 'rejected'
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-amber-100 text-amber-800'
                                    }`}
                                  >
                                    {t(`capitalAddition.status${p.status === 'accepted' ? 'Accepted' : p.status === 'rejected' ? 'Rejected' : 'Pending'}`)}
                                  </span>
                                )}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {t('fundInvestors.investingHolder')}:{' '}
                              {p.investingDisplayName ||
                                (p.investingEntityType === 'company' ? p.investingCompanyId || '—' : t('fundInvestors.investingPersonal'))}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                </div>
              )}
            </div>
          </div>
        </div>
        </EscapeCloseLayer>
      )}

      {contractSummaryModalOpen && modalFundInvestor?.contractFileRef?.storagePath && (
        <EscapeCloseLayer
          onClose={() => {
            if (!contractSummaryLoading) setContractSummaryModalOpen(false);
          }}
        >
        <div
          className="fixed inset-0 z-[230] rentalyzer-modal-overlay rentalyzer-modal-overlay-nested flex items-center justify-center bg-black/50 p-3 sm:p-6"
          onClick={() => !contractSummaryLoading && setContractSummaryModalOpen(false)}
          role="presentation"
        >
          <div
            className="bg-white rounded-xl shadow-xl border border-gray-200 max-w-lg w-full max-h-[min(85vh,640px)] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="fund-contract-summary-title"
          >
            <div className="flex items-start justify-between gap-2 p-4 border-b border-gray-200 shrink-0">
              <h3 id="fund-contract-summary-title" className="text-lg font-semibold text-gray-900 pr-2">
                {t('privateLenderOffer.summaryTitle')}
              </h3>
              <button
                type="button"
                disabled={contractSummaryLoading}
                onClick={() => setContractSummaryModalOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 shrink-0 disabled:opacity-50"
              >
                <span className="sr-only">{t('cancel')}</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 leading-relaxed">
                {t('privateLenderOffer.summaryDisclaimer')}
              </p>
              {contractSummaryError && (
                <p className="text-sm text-red-600">{contractSummaryError}</p>
              )}
              {displayFundContractSummary ? (
                <div className="text-sm text-gray-800 whitespace-pre-wrap border border-gray-100 rounded-lg p-3 bg-gray-50/80">
                  {displayFundContractSummary}
                </div>
              ) : (
                <p className="text-sm text-gray-600">{t('investorFunds.contractSummaryEmptyHint')}</p>
              )}
            </div>
            <div className="p-4 border-t border-gray-100 bg-gray-50/80 shrink-0 flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                disabled={contractSummaryLoading}
                onClick={() => setContractSummaryModalOpen(false)}
                className="px-4 py-2 text-sm text-gray-700 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {t('cancel')}
              </button>
              {!displayFundContractSummary && (
                <button
                  type="button"
                  disabled={contractSummaryLoading}
                  onClick={handleGenerateFundContractSummary}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {contractSummaryLoading ? t('privateLenderOffer.summarizing') : t('privateLenderOffer.summarizeWithAI')}
                </button>
              )}
            </div>
          </div>
        </div>
        </EscapeCloseLayer>
      )}

      <InvestorFundPeriodReportModal
        open={periodReportOpen}
        onClose={() => {
          setPeriodReportOpen(false);
          setPeriodReportKey(null);
        }}
        fundInvestorId={managePlacement?.id || null}
        periodKey={periodReportKey}
      />

      {stackedProfileModalOpen && (
        <ProfileModal
          initialTab="companies"
          onClose={() => setStackedProfileModalOpen(false)}
          overlayClassName="fixed inset-0 z-[240] rentalyzer-modal-overlay rentalyzer-modal-overlay-stacked flex items-center justify-center bg-black/50 p-3 overflow-x-hidden overflow-y-auto"
        />
      )}

      {directChatModal && directChatUser && (
        <DirectChatModal
          onClose={() => setDirectChatModal(null)}
          conversationId={directChatModal.conversationId}
          ensureSpec={directChatModal.ensureSpec}
          title={directChatModal.title}
          subtitle={directChatModal.subtitle}
          currentUser={directChatUser}
        />
      )}
    </div>
  );
}
